import type { StateCreator } from 'zustand/vanilla';
import { SITE } from '@/app/constants';
import type {
  AgentInviteInfoType,
  CountryLevelType,
  CreditV1TeamBundleProduct,
  FreeCreditsTask,
  FreeCreditsTaskGroups,
  NextSubscriptionPlanInfo,
  SubscriptionInfo,
  SubsPlanType,
  TeamSpaceInfo,
  UserInfo,
  UserQuotaInfo,
  ViduV1GetRegionReply,
} from '@/app/types';
import type { PayStore } from './pay';

export type { NextSubscriptionPlanInfo, SubscriptionInfo, UserQuotaInfo } from '@/app/types';

interface CreditsInfo {
  total: number;
  free: number;
  subscribed: number;
  purchased: number;
  credit_sub_expires_at: string | null;
}

interface UserInfoState {
  region: string | null; // 当前地区
  countryLevel: CountryLevelType | null; // 国家等级 t1 t2 t3 unspecified | null 【unspecified 表示未指定/内网/未知】
  userInfo: UserInfo | null;
  // 团队空间信息，null 表示个人空间
  teamSpaceInfo: TeamSpaceInfo | null;
  // 是否团队空间，供全局直接读取
  isTeamSpace: boolean;
  // 当前用户的全部团队空间列表，作为顶部空间切换器等场景的单一数据源
  teamSpaces: TeamSpaceInfo[];
  creditsInfo: CreditsInfo | null;
  subscription: SubscriptionInfo | null;
  // 团队空间下当前生效的 team_bundle 商品
  activeTeamBundleProduct: CreditV1TeamBundleProduct | null;
  nextPlan: NextSubscriptionPlanInfo | null;
  isUserInfoInitialized: boolean; // 避免在未初始化时，由于 userInfo 为 null，误判为未登录
  isSubscriptionInitialized: boolean; // 避免在未初始化时，由于 subscription 为 null
  freeCreditsTasks: FreeCreditsTask[];
  freeCreditsTaskGroups: FreeCreditsTaskGroups | null;
  availableNexus: Array<string>; // 可用的 nexus
  /** nexus 是否已拉取过一次：availableNexus 为 [] 时用于区分"无资格"与"还没拉到" */
  isNexusInitialized: boolean;
  isGiftPlan: boolean; // 是否是赠送套餐
  presentPlanEffectiveTime: Array<string>; // 赠送套餐有效期
  availableCoupons: Array<string>; // 可用优惠券
  bonusCode: string | null; // 福利码
  agentInviteInfo: AgentInviteInfoType | null; // agent邀请信息
  /** 当日观看广告获取积分是否可用（结合 userInfo，null 表示未加载） */
  adWatchCreditEligible: boolean | null;
  userQuotaRefreshVersion: number; // 错峰余量版本标识，提交任务后递增，触发错峰余量重新 fetch
  userQuota: UserQuotaInfo | null; // 错峰余量
}

const isSameTeamSpaceInfo = (prev: TeamSpaceInfo | null, next: TeamSpaceInfo | null): boolean => {
  if (prev === next) {
    return true;
  }
  if (!prev || !next) {
    return false;
  }
  return (
    prev.space_id === next.space_id &&
    prev.team_id === next.team_id &&
    prev.team_name === next.team_name &&
    prev.role === next.role &&
    prev.state === next.state
  );
};

const isSameTeamSpaceList = (prev: TeamSpaceInfo[], next: TeamSpaceInfo[]): boolean => {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  return prev.every((space, index) => isSameTeamSpaceInfo(space, next[index] ?? null));
};

const patchTeamSpaceInList = (spaces: TeamSpaceInfo[], nextTeamSpaceInfo: TeamSpaceInfo | null): TeamSpaceInfo[] => {
  if (!nextTeamSpaceInfo || !nextTeamSpaceInfo.space_id) return spaces;
  let changed = false;
  const result = spaces.map((space) => {
    if (space.space_id !== nextTeamSpaceInfo.space_id) return space;
    if (isSameTeamSpaceInfo(space, nextTeamSpaceInfo)) return space;
    changed = true;
    return { ...space, ...nextTeamSpaceInfo };
  });
  return changed ? result : spaces;
};

export interface UserInfoStore extends UserInfoState {
  updateUserInfo: (userInfo: UserInfo | null) => void; //更新用户信息
  updateUserSubsPlan: (subsPlan: SubsPlanType) => void; //更新用户订阅计划
  updateCreditsInfo: (creditsInfo: CreditsInfo | null) => void; //更新积分
  updateSubscriptionInfo: (subscription: SubscriptionInfo | null) => void; // 更新订阅信息
  updateActiveTeamBundleProduct: (product: CreditV1TeamBundleProduct | null) => void; // 更新当前生效的 team_bundle 商品
  updateIsSubscriptionInitialized: (isSubscriptionInitialized: boolean) => void; // 更新订阅信息是否初始化
  updateNextPlan: (nextPlan: NextSubscriptionPlanInfo | null) => void; // 更新下一个订阅计划
  updateRegion: (region: string | null) => void; // 更新地区
  updateCountryLevel: (countryLevel: NonNullable<ViduV1GetRegionReply['country_level']> | null) => void; // 更新国家等级
  updateFreeCreditsTasks: (freeCreditsTasks: FreeCreditsTask[]) => void; // 更新免费积分任务
  updateFreeCreditsTaskGroups: (freeCreditsTaskGroups: FreeCreditsTaskGroups | null) => void;
  updatePresentPlanEffectiveTime: (presentPlanEffectiveTime: Array<string>) => void; // 更新赠送套餐有效期
  updateAvailableNexus: (availableNexus: Array<string>) => void; // 更新可用的 nexus
  updateIsNexusInitialized: (isNexusInitialized: boolean) => void; // 更新 nexus 是否已初始化
  updateIsGiftPlan: () => void; // 更新是否是赠送套餐
  updateAvailableCoupon: (availableCoupons: Array<string>) => void; // 更新可用优惠券
  updateBonusCode: (bonusCode: string | null) => void; // 更新福利码
  updateAgentInviteInfo: (agentInviteInfo: AgentInviteInfoType | null) => void; // 更新agent邀请信息
  updateAdWatchCreditEligible: (eligible: boolean) => void;
  invalidateUserQuota: () => void; // 提交任务后调用，触发错峰余量重新 fetch
  updateUserQuota: (userQuota: UserQuotaInfo | null) => void; // 更新错峰余量
  updateTeamInfo: (teamInfo: TeamSpaceInfo | null) => void; // 更新团队空间信息（会同步 patch teamSpaces 中匹配条目）
  updateTeamSpaces: (teamSpaces: TeamSpaceInfo[]) => void; // 全量更新团队空间列表（一般由 fetch 回调调用）
}

const initialState: UserInfoState = {
  region: null,
  countryLevel: null,
  userInfo: null,
  teamSpaceInfo: null,
  isTeamSpace: false,
  teamSpaces: [],
  creditsInfo: null,
  subscription: null,
  activeTeamBundleProduct: null,
  nextPlan: null,
  isUserInfoInitialized: false,
  isSubscriptionInitialized: false,
  isGiftPlan: false,
  freeCreditsTasks: [],
  freeCreditsTaskGroups: null,
  availableNexus: [],
  isNexusInitialized: false,
  presentPlanEffectiveTime: [],
  availableCoupons: [],
  bonusCode: null,
  agentInviteInfo: null,
  adWatchCreditEligible: false,
  userQuotaRefreshVersion: 0,
  userQuota: null,
};

export const createUserInfoSlice: StateCreator<UserInfoStore & PayStore, [], [], UserInfoStore> = (
  set,
  get,
  _store,
) => ({
  ...initialState,
  updateUserInfo: (userInfo: UserInfo | null) => {
    if (!userInfo) {
      set({
        userInfo: null,
        teamSpaceInfo: null,
        isTeamSpace: false,
        teamSpaces: [],
        isUserInfoInitialized: true,
        couponSummary: null,
        isNexusInitialized: false,
        freeCreditsTaskGroups: null,
        bonusCode: SITE === 'china' ? null : get().bonusCode,
      });
      return;
    }
    set((state) => {
      const isDifferentUser = Boolean(state.userInfo?.id && state.userInfo.id !== userInfo.id);
      return {
        userInfo,
        ...(isDifferentUser
          ? {
              teamSpaceInfo: null,
              isTeamSpace: false,
              teamSpaces: [],
              isNexusInitialized: false,
              freeCreditsTaskGroups: null,
              bonusCode: SITE === 'china' ? null : state.bonusCode,
            }
          : null),
        isUserInfoInitialized: true,
      };
    });
  },
  updateCreditsInfo: (creditsInfo) => {
    set({ creditsInfo });
  },
  updateUserSubsPlan: (subsPlan: SubsPlanType) => {
    set((state) => {
      if (!state.userInfo) return state;
      return {
        userInfo: {
          ...state.userInfo,
          subs_plan: subsPlan,
        },
      };
    });
  },
  updateSubscriptionInfo: (subscription: SubscriptionInfo | null) => {
    set({ subscription });
  },
  updateActiveTeamBundleProduct: (activeTeamBundleProduct: CreditV1TeamBundleProduct | null) => {
    set({ activeTeamBundleProduct });
  },
  updateIsSubscriptionInitialized: (isSubscriptionInitialized: boolean) => {
    set({ isSubscriptionInitialized });
  },
  updateNextPlan: (nextPlan: NextSubscriptionPlanInfo | null) => {
    set({ nextPlan });
  },
  updateRegion: (region: string | null) => {
    set({ region });
  },
  updateCountryLevel: (countryLevel: NonNullable<ViduV1GetRegionReply['country_level']> | null) => {
    set({ countryLevel });
  },
  updateFreeCreditsTasks: (freeCreditsTasks: FreeCreditsTask[]) => {
    set({ freeCreditsTasks });
  },
  updateFreeCreditsTaskGroups: (freeCreditsTaskGroups: FreeCreditsTaskGroups | null) => {
    set({ freeCreditsTaskGroups });
  },
  updatePresentPlanEffectiveTime: (presentPlanEffectiveTime: Array<string>) => {
    set({ presentPlanEffectiveTime });
  },
  updateAvailableNexus: (availableNexus: Array<string>) => {
    set({ availableNexus: Array.from(new Set([...availableNexus])), isNexusInitialized: true });
  },
  updateIsNexusInitialized: (isNexusInitialized: boolean) => {
    set({ isNexusInitialized });
  },
  updateIsGiftPlan: () => {
    const { userInfo, subscription } = get();
    // 若当前套餐与订阅套餐不一致 && 当前套餐不为免费，则认为当前套餐是运营赠送
    const plan_type = (userInfo?.subs_plan as SubsPlanType) ?? 'free';
    const sub_plan_type = subscription?.plan_type ?? 'free';
    if (sub_plan_type !== plan_type && plan_type !== 'free') {
      set({ isGiftPlan: true });
    } else {
      set({ isGiftPlan: false });
    }
  },
  updateAvailableCoupon: (availableCoupons: Array<string>) => {
    set({ availableCoupons: Array.from(new Set([...availableCoupons])) });
  },
  updateBonusCode: (bonusCode: string | null) => {
    set({ bonusCode });
  },
  updateAgentInviteInfo: (agentInviteInfo: AgentInviteInfoType | null) => {
    set({ agentInviteInfo });
  },
  updateAdWatchCreditEligible: (adWatchCreditEligible: boolean) => {
    set({ adWatchCreditEligible });
  },
  invalidateUserQuota: () => {
    set((state) => ({ userQuotaRefreshVersion: state.userQuotaRefreshVersion + 1 }));
  },
  updateUserQuota: (userQuota) => {
    set({ userQuota });
  },
  updateTeamInfo: (teamSpaceInfo: TeamSpaceInfo | null) => {
    set((state) => {
      const teamInfoChanged = !isSameTeamSpaceInfo(state.teamSpaceInfo, teamSpaceInfo);
      const nextTeamSpaces = patchTeamSpaceInList(state.teamSpaces, teamSpaceInfo);
      const teamSpacesChanged = nextTeamSpaces !== state.teamSpaces;
      if (!teamInfoChanged && !teamSpacesChanged) return state;
      return {
        ...(teamInfoChanged ? { teamSpaceInfo, isTeamSpace: Boolean(teamSpaceInfo) } : null),
        ...(teamSpacesChanged ? { teamSpaces: nextTeamSpaces } : null),
      };
    });
  },
  updateTeamSpaces: (teamSpaces: TeamSpaceInfo[]) => {
    set((state) => {
      if (isSameTeamSpaceList(state.teamSpaces, teamSpaces)) return state;
      return { teamSpaces };
    });
  },
});
